import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "./ui/dialog";
import { ThemeToggle } from "./ThemeToggle";
import { ThemeColorSelector } from "./ThemeColorSelector";
import { ThemeBackgroundSelector } from "./ThemeBackgroundSelector";
import { ThemePresetSelector } from "./ThemePresetSelector";
import type { ThemePreset } from "./ThemePresetSelector";
import { Palette, Sun, Paintbrush, Image, Layers } from "lucide-react";

interface ThemeSettingsModalProps {
  children: React.ReactNode;
}

export function ThemeSettingsModal({ children }: ThemeSettingsModalProps) {
  const [preset, setPreset] = useState<ThemePreset>("custom")

  useEffect(() => {
    const saved = (localStorage.getItem("theme-preset") as ThemePreset) || "custom"
    setPreset(saved)
  }, [])

  // Re-read when modal opens (preset may have changed externally)
  const handleOpenChange = (open: boolean) => {
    if (open) {
      const saved = (localStorage.getItem("theme-preset") as ThemePreset) || "custom"
      setPreset(saved)
    }
  }

  const isCustom = preset === "custom"

  return (
    <Dialog onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        {children}
      </DialogTrigger>
      <DialogContent className="sm:max-w-lg max-h-[80vh] overflow-y-auto p-6">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Palette className="w-5 h-5" />
            Personalizar Tema
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6">

          {/* Preset Themes */}
          <div className="space-y-3">
            <div className="text-center">
              <h3 className="text-base font-semibold flex items-center justify-center gap-2">
                <Layers className="w-4 h-4" />
                Estilo
              </h3>
              <p className="text-xs text-muted-foreground mt-1">
                Presets completos com paleta, tipografia e efeitos visuais
              </p>
            </div>
            <ThemePresetSelector onPresetChange={setPreset} />
          </div>

          {/* Divider */}
          <div className="flex items-center gap-3">
            <div className="flex-1 h-px bg-gradient-to-r from-transparent via-border to-transparent" />
            <Sun className="w-3 h-3 text-muted-foreground" />
            <div className="flex-1 h-px bg-gradient-to-r from-transparent via-border to-transparent" />
          </div>

          {/* Light / Dark mode — always available */}
          <div className="space-y-3">
            <div className="text-center">
              <h3 className="text-base font-semibold flex items-center justify-center gap-2">
                <Sun className="w-4 h-4" />
                Modo
              </h3>
              {!isCustom && (
                <p className="text-xs text-muted-foreground mt-1">
                  Os presets Piano e Dashboard são otimizados para modo escuro
                </p>
              )}
            </div>
            <div className="flex items-center justify-center">
              <ThemeToggle />
            </div>
          </div>

          {/* Custom controls — only shown when preset = custom */}
          {isCustom && (
            <>
              <div className="flex items-center gap-3">
                <div className="flex-1 h-px bg-gradient-to-r from-transparent via-border to-transparent" />
                <Paintbrush className="w-3 h-3 text-muted-foreground" />
                <div className="flex-1 h-px bg-gradient-to-r from-transparent via-border to-transparent" />
              </div>

              <div className="space-y-3">
                <div className="text-center">
                  <h3 className="text-base font-semibold">Cor</h3>
                </div>
                <ThemeColorSelector />
              </div>

              <div className="flex items-center gap-3">
                <div className="flex-1 h-px bg-gradient-to-r from-transparent via-border to-transparent" />
                <Image className="w-3 h-3 text-muted-foreground" />
                <div className="flex-1 h-px bg-gradient-to-r from-transparent via-border to-transparent" />
              </div>

              <div className="space-y-3">
                <div className="text-center">
                  <h3 className="text-base font-semibold">Fundo</h3>
                </div>
                <ThemeBackgroundSelector />
              </div>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}